<template>
  <LoadingSpinner v-if="projectsLoading" />

  <div v-else class="create-invoice">
    <PageHeader title="Add Invoice" />

    <div class="mx-auto max-w-7xl">
      <InvoiceForm
        :project-id="projectId"
        :projects="projects"
        :save-function="createInvoice"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import InvoiceForm from '../component/InvoiceForm.vue'
import { Invoice } from '@/models/invoice'
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import InvoiceService from '@/services/InvoiceService'
import { useNotification } from '@kyvg/vue3-notification'
import { useRoute, useRouter } from 'vue-router'
import PageHeader from '@/components/app/AppPageHeader.vue'
import { useHead } from '@unhead/vue'
import { onUnmounted } from 'vue'

useHead({ title: 'Add Invoice' })

const { notify } = useNotification()
const queryClient = useQueryClient()
const $router = useRouter()
const $route = useRoute()
const projectId = $route.query.projectId as string || ''

const { data: projects, isLoading:projectsLoading } = useQuery({
  queryKey: ['projects'],
  queryFn: () => new ProjectService().all().then((projects) => projects.map((project) => ({ value: project.id, label: project.name, customerId: project.customerId })))
})

const { mutateAsync: createInvoice } = useMutation({
  mutationFn: (newInvoice: Invoice) => new InvoiceService().create(newInvoice),
  onSuccess: (createdInvoice) => {
    queryClient.setQueryData(['invoice', createdInvoice.id], createdInvoice)
    queryClient.invalidateQueries({ queryKey: ['firestore', 'invoices'], exact: false })
    queryClient.invalidateQueries({ queryKey: [ 'invoices', projectId ] })
    notify({
      type: 'success',
      title: 'Invoice created',
      text: 'Invoice created successfully'
    })

    if ($route.query.backTo) {
      $router.push($route.query.backTo as string)
      return
    }
    $router.push({ name: 'ShowInvoice', params: { id: createdInvoice.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error creating invoice',
      text: error.message || 'An error occurred while creating the invoice'
    })
  }
})


onUnmounted(() => {
  queryClient.invalidateQueries({ queryKey: ['invoicedValue'], exact: false })
})
</script>
